import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { AssistantCalculationEvidence } from '@digital-twins/shared-types/assistant'

import { CitationFigure, formatFigureLine, SourceIcon } from '@/components/citations'
import { Heading } from '@/components/ui/Heading'
import { Text } from '@/components/ui/Text'

/**
 * The calculations behind the newest answer (P1): what each figure was
 * computed from, on what base, with the warnings it carries. A data-file figure
 * (D23) also shows the code that ran and its output when the evidence holds a
 * copy of them. Content only, inside the sources drawer's Context section.
 */

interface ThreadCalculationSourcesProps {
  calculations: Array<AssistantCalculationEvidence>
  /** P1: the calculation a clicked figure chip points at, highlighted and scrolled to. */
  highlightedId?: string
  /** Bumps per click, so the same chip clicked again scrolls to it again. */
  focusNonce?: number
}

const SOURCE_LABEL_KEYS = {
  study: 'threads.detail.calculation.sourceStudy',
  run: 'threads.detail.calculation.sourceRun',
  audience: 'threads.detail.calculation.sourceAudience',
  dataset: 'threads.detail.calculation.sourceDataset',
} as const

const WARNING_LABEL_KEYS = {
  unweighted: 'threads.detail.calculation.unweighted',
  unclassified_variable: 'threads.detail.calculation.unclassified',
} as const

function isLabelledWarning(code: string): code is keyof typeof WARNING_LABEL_KEYS {
  return code in WARNING_LABEL_KEYS
}

/** Calculation fields added after the generated types (D4/D5); read defensively. */
interface CalculationDisplayFields {
  sourceName?: string | null
  figures?: Array<CitationFigure> | null
}

const MAX_CARD_FIGURES = 3

function displayFields(calculation: AssistantCalculationEvidence): { sourceName: string | null; figures: Array<CitationFigure> } {
  const raw = calculation as AssistantCalculationEvidence & CalculationDisplayFields
  const sourceName = typeof raw.sourceName === 'string' && raw.sourceName.trim() ? raw.sourceName.trim() : null
  const figures = Array.isArray(raw.figures)
    ? raw.figures.filter((figure): figure is CitationFigure =>
        Boolean(figure) && typeof figure.label === 'string' && typeof figure.unit === 'string' &&
        typeof figure.value === 'number' && Number.isFinite(figure.value))
    : []
  return { sourceName, figures }
}

export function ThreadCalculationSources(props: ThreadCalculationSourcesProps) {
  const { calculations, highlightedId, focusNonce } = props

  const highlightedRef = useRef<HTMLDivElement | null>(null)

  const { t, i18n } = useTranslation('app')

  useEffect(() => {
    highlightedRef.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' })
  }, [highlightedId, focusNonce])

  if (calculations.length === 0) {
    return null
  }

  return (
    <div className="space-y-3 px-4 pb-4 pt-2" data-testid="thread-calculation-sources">
      <Heading level={2} size="sm" weight="medium">
        {t('threads.detail.calculation.heading')}
      </Heading>
      {calculations.map((calculation, index) => {
        const warnings = (calculation.warnings ?? [])
          .filter(isLabelledWarning)
          .map((code) => WARNING_LABEL_KEYS[code])
        const sandbox = calculation.sandbox
        const isHighlighted = calculation.id === highlightedId
        const { sourceName, figures } = displayFields(calculation)
        const figureLines = figures
          .slice(0, MAX_CARD_FIGURES)
          .map((figure) => formatFigureLine(figure, t, i18n.language))
        const quote = figureLines.length > 0 ? (
          <blockquote
            className="border-l-2 border-muted-foreground/30 bg-muted/40 px-2 py-1 text-xs text-foreground"
            data-testid={`thread-calculation-source-${index}-figures`}
          >
            {figureLines.map((line, lineIndex) => (
              <span key={`${calculation.id}-figure-${lineIndex}`} className="block">
                {line}
              </span>
            ))}
          </blockquote>
        ) : calculation.snippet ? (
          <blockquote
            className="border-l-2 border-muted-foreground/30 bg-muted/40 px-2 py-1 text-xs text-foreground"
            data-testid={`thread-calculation-source-${index}-snippet`}
          >
            {calculation.snippet}
          </blockquote>
        ) : null
        return (
          <div
            key={calculation.id}
            ref={isHighlighted ? highlightedRef : undefined}
            className={
              isHighlighted
                ? 'space-y-1 rounded-lg border border-foreground/40 p-3 ring-1 ring-muted-foreground/30'
                : 'space-y-1 rounded-lg border border-border p-3'
            }
            data-highlighted={isHighlighted ? 'true' : undefined}
            data-testid={`thread-calculation-source-${index}`}
          >
            {/* The source the figure was computed from. Without a name the
                meta line below already says its kind, so it is not repeated. */}
            {sourceName ? (
              <div className="flex items-center gap-1.5" data-testid={`thread-calculation-source-${index}-source`}>
                <SourceIcon source={{ kind: calculation.source.kind }} className="h-3.5 w-3.5" />
                <Text as="span" size="xs" color="muted" className="truncate">
                  {sourceName}
                </Text>
              </div>
            ) : null}
            <Text size="sm" weight="medium">
              {calculation.title || t('threads.detail.calculation.fallbackTitle')}
            </Text>
            <Text size="xs" color="muted" data-testid={`thread-calculation-source-${index}-meta`}>
              {t(SOURCE_LABEL_KEYS[calculation.source.kind])}
              {' · '}
              {t('threads.detail.calculation.base', { count: calculation.base })}
            </Text>
            {quote}
            {warnings.length > 0 ? (
              <Text size="xs" className="text-chart-warning" data-testid={`thread-calculation-source-${index}-warnings`}>
                {warnings.map((key) => t(key)).join(' · ')}
              </Text>
            ) : null}
            {sandbox ? (
              <details data-testid={`thread-calculation-source-${index}-sandbox`}>
                <summary
                  className="cursor-pointer text-xs text-muted-foreground"
                  data-testid={`thread-calculation-source-${index}-sandbox-toggle`}
                >
                  {t('threads.detail.calculation.codeAndOutput')}
                </summary>
                {sandbox.code ? (
                  <div className="pt-2">
                    <Text size="xs" color="muted">{t('threads.detail.calculation.code')}</Text>
                    <pre className="mt-1 max-h-60 overflow-auto rounded-md bg-muted p-2 text-[11px]">
                      <code>{sandbox.code}</code>
                    </pre>
                  </div>
                ) : null}
                {sandbox.output ? (
                  <div className="pt-2">
                    <Text size="xs" color="muted">{t('threads.detail.calculation.output')}</Text>
                    <pre className="mt-1 max-h-60 overflow-auto rounded-md bg-muted p-2 text-[11px]">
                      <code>{sandbox.output}</code>
                    </pre>
                  </div>
                ) : null}
              </details>
            ) : null}
          </div>
        )
      })}
    </div>
  )
}
